import { LinkOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Statistic, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import { memo, useMemo, useState } from "react";
import { DataGrid } from "../../components/data-grid/DataGrid";
import { DataGridFilterButton } from "../../components/data-grid/DataGridFilterButton";
import { DataGridFilterDrawer } from "../../components/data-grid/DataGridFilterDrawer";
import { DataGridSearch } from "../../components/data-grid/DataGridSearch";
import { DataGridToolbar } from "../../components/data-grid/DataGridToolbar";
import { DataGridRowAction } from "../../components/data-grid/types";
import { SortDirection } from "../../entities/common";
import {
  InvestmentMatchReviewFamily,
  InvestmentMatchReviewGroup,
  InvestmentMatchReviewRequest,
  InvestmentReportDatasetType,
} from "../../entities/investmentReview";
import {
  useGetInvestmentReviewQueueQuery,
  useGetInvestmentReviewQueueSummaryQuery,
} from "../../features/api/investmentReviewApi";
import { InvestmentReviewDecisionModal } from "./InvestmentReviewDecisionModal";
import { InvestmentReviewFilters } from "./InvestmentReviewFilters";

const datasetLabels: Record<number, string> = {
  [InvestmentReportDatasetType.IndustrySummary]: "صنایع",
  [InvestmentReportDatasetType.ListedPortfolio]: "بورسی",
  [InvestmentReportDatasetType.UnlistedPortfolio]: "خارج بورس",
  [InvestmentReportDatasetType.AcquiredShares]: "تحصیل",
  [InvestmentReportDatasetType.DisposedShares]: "واگذاری",
  [InvestmentReportDatasetType.DividendIncome]: "سود سهام",
};

type DecisionMode = "resolve" | "create" | "ignore";

export const InvestmentReviewTable = memo(() => {
  const [request, setRequest] = useState<InvestmentMatchReviewRequest>({
    pageNumber: 1,
    pageSize: 20,
    search: "",
    includeUnmatched: true,
    includeNeedsReview: true,
    sortBy: "occurrenceCount",
    sortDirection: SortDirection.Desc,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<InvestmentMatchReviewRequest>(request);
  const [decision, setDecision] = useState<{ mode?: DecisionMode; group?: InvestmentMatchReviewGroup }>({});

  const { data, isLoading, isFetching } = useGetInvestmentReviewQueueQuery(request);
  const { data: summary, isFetching: summaryFetching } = useGetInvestmentReviewQueueSummaryQuery(request);

  const columns = useMemo<ColumnsType<InvestmentMatchReviewGroup>>(
    () => [
      {
        title: "عنوان منبع",
        dataIndex: "normalizedTitle",
        key: "normalizedTitle",
        sorter: true,
        width: 330,
        render: (_, row) => (
          <div>
            <div className="font-medium">{row.sampleSourceTitles?.[0] ?? row.normalizedTitle}</div>
            <div className="mt-1 text-xs text-slate-500 break-all">{row.normalizedTitle}</div>
          </div>
        ),
      },
      {
        title: "نوع",
        dataIndex: "family",
        key: "family",
        sorter: true,
        width: 120,
        render: (family) => (family === InvestmentMatchReviewFamily.Industry ? <Tag color="purple">Industry</Tag> : <Tag color="blue">Company</Tag>),
      },
      {
        title: "Occurrence",
        dataIndex: "occurrenceCount",
        key: "occurrenceCount",
        sorter: true,
        width: 110,
      },
      {
        title: "گزارش",
        dataIndex: "reportCount",
        key: "reportCount",
        sorter: true,
        width: 90,
      },
      {
        title: "Unmatched",
        dataIndex: "unmatchedCount",
        key: "unmatchedCount",
        sorter: true,
        width: 100,
      },
      {
        title: "Review",
        dataIndex: "needsReviewCount",
        key: "needsReviewCount",
        sorter: true,
        width: 90,
        render: (value) => (value ? <Tag color="orange">{value}</Tag> : 0),
      },
      {
        title: "Datasetها",
        key: "datasets",
        width: 260,
        render: (_, row) => (
          <Space size={[2, 4]} wrap>
            {row.datasetTypes.map((type) => <Tag key={type}>{datasetLabels[type] ?? type}</Tag>)}
          </Space>
        ),
      },
      {
        title: "فقط خارج بورس",
        dataIndex: "isUnlistedOnly",
        key: "isUnlistedOnly",
        sorter: true,
        width: 110,
        render: (value) => (value ? <Tag color="cyan">بله</Tag> : "—"),
      },
    ],
    [],
  );

  const rowActions = useMemo<DataGridRowAction<InvestmentMatchReviewGroup>[]>(
    () => [
      {
        key: "resolve",
        title: "اتصال به هویت موجود",
        icon: <LinkOutlined />,
        onClick: (group) => setDecision({ mode: "resolve", group }),
      },
      {
        key: "create-other-company",
        title: "ساخت OtherCompany",
        icon: <PlusOutlined />,
        hidden: (group) => group.family !== InvestmentMatchReviewFamily.CompanyLike,
        onClick: (group) => setDecision({ mode: "create", group }),
      },
      {
        key: "ignore",
        title: "Ignore پایدار",
        icon: <StopOutlined />,
        danger: true,
        onClick: (group) => setDecision({ mode: "ignore", group }),
      },
    ],
    [],
  );

  const applyFilters = () => {
    setRequest({ ...draftFilters, pageNumber: 1 });
    setFiltersOpen(false);
  };

  const resetFilters = () => {
    const next: InvestmentMatchReviewRequest = {
      pageNumber: 1,
      pageSize: request.pageSize,
      search: request.search,
      includeUnmatched: true,
      includeNeedsReview: true,
      sortBy: request.sortBy,
      sortDirection: request.sortDirection,
    };
    setDraftFilters(next);
    setRequest(next);
    setFiltersOpen(false);
  };

  return (
    <div>
      <Row gutter={[12, 12]} className="mb-4">
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="گروه‌ها" value={summary?.groupCount ?? 0} /></Card></Col>
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="Occurrence" value={summary?.occurrenceCount ?? 0} /></Card></Col>
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="Unmatched" value={summary?.unmatchedOccurrenceCount ?? 0} /></Card></Col>
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="Needs Review" value={summary?.needsReviewOccurrenceCount ?? 0} /></Card></Col>
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="Company groups" value={summary?.companyLikeGroupCount ?? 0} /></Card></Col>
        <Col xs={12} lg={4}><Card size="small" loading={summaryFetching}><Statistic title="Unlisted only" value={summary?.unlistedOnlyCompanyGroupCount ?? 0} /></Card></Col>
      </Row>

      <DataGridToolbar
        title={<strong>Investment Data Quality Queue</strong>}
        search={
          <DataGridSearch
            value={request.search}
            placeholder="جستجو در عنوان normalized"
            onSearch={(search) => setRequest({ ...request, pageNumber: 1, search })}
          />
        }
        filters={
          <DataGridFilterButton
            activeFiltersCount={[
              request.family,
              request.datasetType,
              request.isUnlistedOnly,
              request.includeUnmatched ? undefined : false,
              request.includeNeedsReview ? undefined : false,
            ].filter((x) => x !== undefined).length}
            onClick={() => {
              setDraftFilters(request);
              setFiltersOpen(true);
            }}
          />
        }
      />

      <DataGrid
        request={request}
        onRequestChange={setRequest}
        data={data}
        columns={columns}
        rowActions={rowActions}
        loading={isLoading || isFetching}
        pageSizeOptions={[10, 20, 50, 100, 250]}
      />

      <DataGridFilterDrawer open={filtersOpen} onClose={() => setFiltersOpen(false)} title="فیلترهای Investment Review">
        <InvestmentReviewFilters filters={draftFilters} onChange={setDraftFilters} />
        <div className="flex gap-2">
          <Button type="primary" className="flex-1" onClick={applyFilters}>اعمال</Button>
          <Button className="flex-1" onClick={resetFilters}>پاک کردن</Button>
        </div>
      </DataGridFilterDrawer>

      <InvestmentReviewDecisionModal mode={decision.mode} group={decision.group} onClose={() => setDecision({})} />
    </div>
  );
});
